import React from 'react';
import { Box, Typography } from '@mui/material';

export interface ImageMetadata {
  latitude?: number;
  longitude?: number;
  address?: string;
  capturedAt: Date;
}

export interface MetadataOverlayProps {
  id: string;
  metadata: ImageMetadata;
  rect: DOMRect;
  scrollOffset?: { x: number; y: number };
  overlayScale?: number; // Custom scale factor (default: 1.0)
}

export const MetadataOverlay: React.FC<MetadataOverlayProps> = ({ 
  id, 
  metadata, 
  rect, 
  scrollOffset = { x: 0, y: 0 },
  overlayScale = 1.0 // Default scale is 1.0
}) => {
  // Match CSS system proportions - using CSS overlay scale logic with custom scale
  const baseOverlayScale = 0.5; // Base scale from CSS system
  const effectiveScale = baseOverlayScale * overlayScale; // Apply user's custom scale
  const scaleFactor = rect.width / 133; // Base reference size like CSS system
  
  // Make overlay width scale with the overlay scale (base 2/3 of image width)
  const baseOverlayWidthRatio = 2/3; // Base width is 2/3 of image
  const overlayWidth = rect.width * baseOverlayWidthRatio * overlayScale; // Scale the width too
  const baseOverlayHeight = 52; // Base height from CSS system
  const overlayHeight = baseOverlayHeight * effectiveScale * scaleFactor;

  // Position at bottom-left like CSS system
  const leftPosition = rect.left + (8 * effectiveScale * scaleFactor); // Match CSS padding
  const topPosition = rect.top + rect.height - overlayHeight - (8 * effectiveScale * scaleFactor);
  
  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    left: Math.max(0, leftPosition),
    top: Math.max(0, topPosition),
    width: overlayWidth,
    height: overlayHeight,
    zIndex: 1000,
    pointerEvents: 'none',
  };

  // Parse address components
  const parseAddress = (address: string) => {
    if (!address) return { street: '', city: '', state: '', zip: '' };
    
    const parts = address.split(', ');
    
    if (parts.length >= 4) {
      // Format: "Street, City, State ZIP"
      const street = parts[0];
      const city = parts[1];
      const stateZip = parts[2];
      const zipMatch = stateZip.match(/([A-Z]{2})\s+(\d{5})/);
      
      if (zipMatch) {
        return {
          street,
          city,
          state: zipMatch[1],
          zip: zipMatch[2]
        };
      }
    }
    
    // Fallback parsing
    return {
      street: parts[0] || '',
      city: parts[1] || '',
      state: parts[2] || '',
      zip: ''
    };
  };

  // Format the capture time
  const formatCaptureTime = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate font sizes to match CSS system scaling
  const cityStateFontSize = 9 * effectiveScale * scaleFactor;
  const streetFontSize = 8 * effectiveScale * scaleFactor;
  const zipFontSize = 8 * effectiveScale * scaleFactor;
  const timestampFontSize = 7 * effectiveScale * scaleFactor;
  
  // Map size to match CSS system (40px base size)
  const mapSize = 40 * effectiveScale * scaleFactor;

  // Create OpenStreetMap tile URL like CSS system
  const createMapUrl = () => {
    if (!metadata.latitude || !metadata.longitude) return null;
    
    const zoom = 16; // Match CSS system zoom level
    const lat = metadata.latitude;
    const lon = metadata.longitude;
    
    // Web Mercator projection calculation (matching CSS system)
    const tileX = Math.floor((lon + 180.0) / 360.0 * Math.pow(2.0, zoom));
    const tileY = Math.floor((1.0 - Math.log(Math.tan(lat * Math.PI / 180.0) + 
      1.0 / Math.cos(lat * Math.PI / 180.0)) / Math.PI) / 2.0 * Math.pow(2.0, zoom));
    
    return `https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`;
  };

  return (
    <Box
      sx={{
        ...overlayStyle,
        // Match CSS system styling exactly
        background: 'rgba(0, 0, 0, 0.85)', // Match CSS background
        color: 'white',
        borderRadius: `${3 * effectiveScale * scaleFactor}px`, // Match CSS border-radius calc
        padding: `${8 * effectiveScale * scaleFactor}px ${10 * effectiveScale * scaleFactor}px ${8 * effectiveScale * scaleFactor}px ${8 * effectiveScale * scaleFactor}px`, // Match CSS padding
        border: '1px solid rgba(255, 255, 255, 0.2)', // Match CSS border
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)', // Match CSS shadow
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', // Match CSS font stack
        display: 'flex',
        flexDirection: 'row',
        gap: `${6 * effectiveScale * scaleFactor}px`, // Match CSS gap calc
        alignItems: 'center', // Center the map vertically within the overlay
        fontWeight: 'normal', // Match CSS system
      }}
    >
      {/* Map Tile - Match CSS system exactly */}
      {metadata.latitude && metadata.longitude && (
        <Box 
          sx={{ 
            width: mapSize,
            height: mapSize,
            borderRadius: `${3 * effectiveScale * scaleFactor}px`, // Match CSS system
            overflow: 'hidden',
            bgcolor: '#333', // Match CSS fallback bg
            border: '1px solid rgba(255, 255, 255, 0.3)', // Match CSS border
            flexShrink: 0,
            position: 'relative',
          }}
        >
          {/* OpenStreetMap Tile - matching CSS system */}
          <Box
            component="img"
            src={createMapUrl() || undefined}
            alt="Map tile"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
            onError={(e) => {
              // Fallback background like CSS system
              const target = e.target as HTMLElement;
              target.style.display = 'none';
            }}
          />

          {/* Location Pin - Match CSS system styling exactly */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: `${6 * effectiveScale * scaleFactor}px`, // Match CSS .metadata-location-pin
              height: `${6 * effectiveScale * scaleFactor}px`,
              background: 'white', // Outer white circle
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              '&::after': {
                content: '""',
                position: 'absolute',
                width: `${4 * effectiveScale * scaleFactor}px`, // Inner red dot
                height: `${4 * effectiveScale * scaleFactor}px`,
                background: 'rgb(255, 68, 68)', // iOS red from CSS
                borderRadius: '50%',
              }
            }}
          />
        </Box>
      )}

      {/* Text Content - Match CSS system 4-row layout exactly */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between',
        minWidth: 0,
        height: '100%',
      }}>
        
        {metadata.address && (() => {
          const addressParts = parseAddress(metadata.address);
          
          return (
            <>
              {/* City, State - matches .metadata-city-state */}
              <Typography sx={{ 
                fontSize: `${cityStateFontSize}px`, 
                lineHeight: 1.1,
                fontWeight: 'bold', // Match CSS system
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'white', // Pure white like CSS
                margin: 0,
                padding: 0,
              }}>
                {addressParts.city}{addressParts.state && `, ${addressParts.state}`}
              </Typography>

              {/* Street Address - matches .metadata-street */}
              <Typography sx={{ 
                fontSize: `${streetFontSize}px`, 
                lineHeight: 1.1,
                fontWeight: 'normal', // Match CSS system
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'rgba(255, 255, 255, 0.9)', // Match CSS system
                margin: 0,
                padding: 0,
              }}>
                {addressParts.street}
              </Typography>

              {/* ZIP Code - matches .metadata-zip */}
              {addressParts.zip && (
                <Typography sx={{ 
                  fontSize: `${zipFontSize}px`, 
                  lineHeight: 1.1,
                  fontWeight: 'normal', // Match CSS system
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'rgba(255, 255, 255, 0.9)', // Match CSS system
                  margin: 0,
                  padding: 0,
                }}>
                  {addressParts.zip}
                </Typography>
              )}

              {/* Capture Time - matches .metadata-timestamp */}
              <Typography sx={{ 
                fontSize: `${timestampFontSize}px`,
                fontWeight: 'normal', // Match CSS system
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'rgba(255, 255, 255, 0.8)', // Match CSS system
                margin: 0,
                padding: 0,
                textAlign: 'left', // Ensure left alignment
              }}>
                {formatCaptureTime(metadata.capturedAt)}
              </Typography>
            </>
          );
        })()}
        
        {/* Fallback when no address */}
        {!metadata.address && (
          <>
            <Typography sx={{ 
              fontSize: `${cityStateFontSize}px`, 
              lineHeight: 1.1,
              fontWeight: 'bold',
              color: 'white',
              margin: 0,
              padding: 0,
            }}>
              Location Unknown
            </Typography>
            
            <Typography sx={{ 
              fontSize: `${timestampFontSize}px`,
              fontWeight: 'normal',
              lineHeight: 1.1,
              color: 'rgba(255, 255, 255, 0.8)',
              margin: 0,
              padding: 0,
              textAlign: 'left', // Ensure left alignment
            }}>
              {formatCaptureTime(metadata.capturedAt)}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
